import React, { FC, useCallback } from 'react';
import styled from 'styled-components';

import ActionRow from './ActionRow';
import { ActionType, CreateTaskAction as CreateTaskActionType } from 'src/actions/rules/actions';
import InputField, { InputType } from 'src/presentation/components/forms/InputField';
import { StyledDropdown, StyledTypeDescription } from 'src/presentation/components/pages/rules/shared/StyledComponents';
import Textarea from 'src/presentation/components/forms/Textarea';
import useAgents from 'src/presentation/components/pages/rules/hooks/useAgents';
import useRoutingGroups from 'src/presentation/components/pages/rules/hooks/useRoutingGroups';

type CreateTaskActionProps = {
  action: CreateTaskActionType;
  onDelete: Function;
  onUpdate: Function;
};

const CreateTaskAction: FC<CreateTaskActionProps> = ({ action, onDelete, onUpdate }) => {
  const { loadingState: agentsLoading, values: agentOptions } = useAgents();
  const { loadingState: groupsLoading, values: routingGroupOptions } = useRoutingGroups();

  const onChangeMinutes = useCallback(
    value => {
      const mins = Math.abs(parseInt(value)) || 0;
      action.updateDueInMinutes(mins);
      onUpdate(ActionType.CreateTask, action);
    },
    [action, onUpdate]
  );

  const onChangeNote = useCallback(
    e => {
      action.updateNote(e.target.value);
      onUpdate(ActionType.CreateTask, action);
    },
    [action, onUpdate]
  );

  const onChangeAgent = useCallback(
    agentId => {
      const assignee = action.assignee;
      assignee.setAgentId(agentId);
      action.updateAssignee(assignee);
      onUpdate(ActionType.CreateTask, action);
    },
    [action, onUpdate]
  );

  const onChangeGroup = useCallback(
    groupId => {
      const assignee = action.assignee;
      assignee.setRoutingGroupId(groupId);
      action.updateAssignee(assignee);
      onUpdate(ActionType.CreateTask, action);
    },
    [action, onUpdate]
  );

  if (agentsLoading === 'loading' || groupsLoading === 'loading') return null;

  return (
    <CreateTaskRow type={ActionType.CreateTask} onDelete={onDelete}>
      <Wrapper>
        <Row>
          <StyledTypeDescription>Create a Task</StyledTypeDescription>
          <StyledTypeDescription>Due In</StyledTypeDescription>
          <InputWrapper>
            <InputField
              attr="minutes"
              onChange={onChangeMinutes}
              type={InputType.number}
              value={action.dueInMinutes.toString()}
            />
          </InputWrapper>
          <StyledTypeDescription>Minutes</StyledTypeDescription>
        </Row>
        <Row>
          Assign to
          <AssignmentDropdown
            maxRows={5}
            onSelect={onChangeGroup}
            options={routingGroupOptions}
            placeholder="Select inbox"
            searchable
            value={action.assignee.routingGroupId}
          />
          <AssignmentDropdown
            maxRows={5}
            onSelect={onChangeAgent}
            options={agentOptions}
            placeholder="Select agent"
            searchable
            value={action.assignee.agentId}
          />
        </Row>
        <NoteRow>
          Task Is
          <StyledTextarea
            attr="note"
            onChange={onChangeNote}
            placeholder="Enter task description"
            value={action.note}
          />
        </NoteRow>
      </Wrapper>
    </CreateTaskRow>
  );
};

const Wrapper = styled.div`
  width: 100%;
`;

const CreateTaskRow = styled(ActionRow)`
  align-items: baseline;
`;

const Row = styled.div`
  align-items: center;
  display: flex;
  padding-bottom: 12px;
`;

const NoteRow = styled(Row)`
  align-items: flex-start;
`;

const InputWrapper = styled.div`
  margin-right: 12px;
  width: 50px;
`;

const AssignmentDropdown = styled(StyledDropdown)`
  margin-left: 12px;
  width: 175px;
`;

const StyledTextarea = styled(Textarea)`
  margin-left: 27px;
`;

export default CreateTaskAction;
